import * as turf from '@turf/turf';
import { FeatureCollection, Feature } from 'geojson';

export interface BufferOptions {
  distance: number;
  units: turf.Units;
  steps?: number;
}

/**
 * Create buffer around features
 * - Point → Circle polygon
 * - LineString → Polygon corridor
 * - Polygon → Expanded polygon
 */
export function bufferFeatures(
  geojson: FeatureCollection,
  options: BufferOptions
): FeatureCollection {
  const { distance, units, steps = 64 } = options;
  
  const buffered: Feature[] = [];
  
  geojson.features.forEach((feature) => {
    try {
      const result = turf.buffer(feature, distance, { units, steps });
      
      if (result) {
        // Preserve original properties
        buffered.push({
          ...result,
          properties: {
            ...feature.properties,
            buffer_distance: distance,
            buffer_units: units
          }
        });
      }
    } catch {
      // Skip features that cannot be buffered (e.g. null geometry)
    }
  });
  
  return {
    type: 'FeatureCollection',
    features: buffered,
    ...(geojson as any).crs && { crs: (geojson as any).crs }
  } as FeatureCollection;
}

/**
 * Get appropriate default units based on CRS
 */
export function getDefaultUnits(epsgCode?: number): turf.Units {
  if (!epsgCode || epsgCode === 4326) {
    return 'kilometers';
  }
  
  // For projected coordinate systems (like UTM, State Plane, etc.)
  if (epsgCode >= 32601 && epsgCode <= 32660) return 'meters'; // UTM North
  if (epsgCode >= 32701 && epsgCode <= 32760) return 'meters'; // UTM South
  if (epsgCode === 3857) return 'meters'; // Web Mercator
  
  return 'meters';
}

/**
 * Validate buffer distance
 */
export function validateBufferDistance(
  distance: number,
  units: turf.Units
): { valid: boolean; message?: string } {
  // Reject non-finite values (NaN, Infinity)
  if (!Number.isFinite(distance)) {
    return {
      valid: false,
      message: 'Buffer distance must be a finite number'
    };
  }
  // Reject zero (no-op) but allow negative for inward buffering
  if (distance === 0) {
    return {
      valid: false,
      message: 'Buffer distance must be non-zero (use negative for inward buffer)'
    };
  }
  
  // Warn about very large buffers
  if (units === 'kilometers' && distance > 1000) {
    return {
      valid: false,
      message: 'Buffer distance seems too large (>1000 km)'
    };
  }
  
  if (units === 'meters' && distance > 1000000) {
    return {
      valid: false,
      message: 'Buffer distance seems too large (>1000 km)'
    };
  }
  
  return { valid: true };
}
